import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Shield,
  Brain,
  Palette,
  GitBranch,
  Users,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

export function BlogAcquisitionCTA() {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: Sparkles,
      title: 'AI Ad Generation',
      description: 'Create professional, compliant ads from scratch in seconds. Export-ready designs for any platform.',
      color: 'from-purple-500 to-blue-500'
    },
    {
      icon: Shield,
      title: 'Multi-Platform Compliance',
      description: 'Instant compliance checking across Meta, Google, TikTok, YouTube, Pinterest, and Microsoft Ads.',
      color: 'from-purple-500 to-blue-500'
    },
    {
      icon: Brain,
      title: 'AI Performance Predictions',
      description: 'Know exactly how your ads will perform before spending a dollar. Get platform-specific scores.',
      color: 'from-purple-500 to-blue-500'
    },
    {
      icon: Palette,
      title: 'Creative Enhancement',
      description: 'AI-powered suggestions and automatic creative improvements for better performance.',
      color: 'from-purple-500 to-blue-500'
    },
    {
      icon: GitBranch,
      title: 'Complete Versioning',
      description: 'Track every iteration of your creatives with complete version history and rollback options.',
      color: 'from-purple-500 to-blue-500'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Invite teammates, manage permissions, and collaborate seamlessly across your organization.',
      color: 'from-purple-500 to-blue-500'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="mt-16 pt-16 border-t border-white/20"
      data-blog-acquisition-cta
    >
      <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-lg rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 lg:p-12 border border-purple-500/20 hover:border-purple-500/40 transition-all">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="text-center mb-6 sm:mb-8 lg:mb-12"
        >
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4 leading-tight">
            Ready to Transform Your
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> Ad Creative Process?</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto px-2">
            Join 1000+ marketers who create compliant, high-performing ads in minutes with our AI-powered platform
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8 lg:mb-12">
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 border border-white/10 hover:bg-white/10 transition-all group"
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r ${benefit.color} rounded-lg flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform`}>
                  <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2 leading-tight">
                  {benefit.title}
                </h3>
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center"
        >

          {/* Trust Elements */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-2 sm:gap-4 text-xs sm:text-sm md:text-base text-gray-400">
            <div className="flex items-center">
              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 mr-1" />
              <span className="text-green-400 font-semibold">No credit card required</span>
            </div>
            <span className="hidden sm:inline text-gray-500">•</span>
            <div className="flex items-center">
              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 mr-1" />
              <span className="text-green-400 font-semibold">3 free credits</span>
            </div>
            <span className="hidden sm:inline text-gray-500">•</span>
            <div className="flex items-center">
              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 mr-1" />
              <span className="text-green-400 font-semibold">Full platform access</span>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}